import { Autocomplete, TextField } from '@mui/material';

export interface DropdownOption {
  id: string | number;
  name: string;
  displayName?: string;
}

interface Props {
  label: string;
  options: DropdownOption[];
  disabled?: boolean;
  onChange: (id: string | number | (string | number)[] | null) => void;
  value?: string | number | (string | number)[] | null;
  showError?: boolean;
  errorText?: string;
  multiple?: boolean;
  disableClearable?: boolean;
  size?: 'small' | 'medium';
}

const Dropdown = ({
  label,
  options,
  disabled,
  onChange,
  value,
  showError,
  errorText,
  multiple,
  disableClearable,
  size,
}: Props) => {
  const findOption = (id: string | number): DropdownOption | undefined =>
    options.find((opt) => opt.id === id);

  let mappedValue: DropdownOption | DropdownOption[] | null;

  if (multiple) {
    if (Array.isArray(value)) {
      mappedValue = value
        .map((val) => findOption(val))
        .filter((opt): opt is DropdownOption => opt !== undefined); // filter out undefined
    } else {
      mappedValue = [];
    }
  } else {
    mappedValue =
      value != null ? (findOption(value as string | number) ?? null) : null;
  }

  return (
    <Autocomplete
      disabled={disabled}
      disableClearable={disableClearable}
      size={multiple ? 'small' : size}
      options={options ?? []}
      getOptionLabel={(option) => option.displayName ?? option.name}
      isOptionEqualToValue={(option, val) => option.id === val.id}
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          {option.displayName ?? option.name}
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          size="small"
          error={!!showError}
          helperText={errorText}
        />
      )}
      onChange={(_, selected) => {
        if (multiple) {
          const ids = Array.isArray(selected) ? selected.map((s) => s.id) : [];
          onChange(ids);
        } else {
          const id = (selected as DropdownOption | null)?.id ?? null;
          onChange(id);
        }
      }}
      value={mappedValue}
      multiple={multiple}
      filterSelectedOptions={multiple}
    />
  );
};

export default Dropdown;
